import { Component, ViewChild, OnInit, HostListener, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA } from "@angular/core";
import { CommonModule } from "@angular/common";
import { Router, ActivatedRoute } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ICountry } from "../../../interfaces/icountry";
import { Metadata, SourceMetadata, MetaCountry } from "../../../models/metadata";
import { DropDownItem } from "../../../models/dropdown";
import { MetadataType } from "../../../enums/metadatatype";
import { MetadataService } from "../../../services/metadata.service";
import { AppSettings } from "../../../appsettings";

import { ICanComponentDeactivate } from "../../../interfaces/icancomponentdeactivate";
import { Observable } from "rxjs";
import { ConfirmationModalComponent } from "../../confirmationmodal/confirmationmodal.component";
import { NgSelectComponent } from "../../shared/ng-select/ng-select.component";
import { HeaderComponent } from "../../header/header.component";
/**
 * Event component
 */
@Component({
    selector: "metadataconfig",
    templateUrl: "./metadataconfig.component.html",
    styles: [`
        
        `],
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, ConfirmationModalComponent, NgSelectComponent, HeaderComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA]
})
export class MetadataConfigComponent implements OnInit, ICanComponentDeactivate {
    allCountries: ICountry[] = new Array();
    metadata: Metadata;
    metadataForm: FormGroup;
    metadataType: any = MetadataType;
    metadataTypes: DropDownItem[] = new Array();
    selectedType: DropDownItem = null;
    title: string;
    selectAll: boolean = false;
    disabled = false;
    isLoading = true; // Add loading state
    @ViewChild("confirmModal") confirmModal;

    // ng-select items for type dropdown
    activeType: any[] = [];

    constructor(private toastr: ToastrService, private activatedRoute: ActivatedRoute, private router: Router, private metadataService: MetadataService) {
    }



    /**
     * On initialise of metadata component.
     * Here it initialises the form contents and load the data to display.
     */
    ngOnInit(): void {
        // Generate dropdown items from MetadataType enum
        this.metadataTypes = [];
        for (const key in this.metadataType) {
            if (isNaN(Number(key))) {
                // key is the enum name (e.g., "Source")
                const enumValue = this.metadataType[key];
                this.metadataTypes.push(new DropDownItem(enumValue, key, null));
            }
        }

        this.allCountries = this.activatedRoute.snapshot.data["countries"];
        this.metadata = this.activatedRoute.snapshot.data["metadata"];
        this.disabled = this.metadata ? this.metadata.isEditable : false;
        this.title = this.metadata != null ? "Update metadata" : "Create metadata";
        
        // Initialize form with proper type value
        const initialTypeValue = this.metadata ? this.metadata.metadataType : (this.metadataTypes.length > 0 ? this.metadataTypes[0].id : null);
        
        this.metadataForm = new FormGroup({
            id: new FormControl(this.metadata ? this.metadata.id : 0),
            type: new FormControl(initialTypeValue, [Validators.required]),
            name: new FormControl({ value: this.metadata ? this.metadata.name : "", disabled: this.disabled }, [Validators.required]),
            disabled: new FormControl(this.metadata ? this.metadata.disabled : false),
            countries: new FormGroup({})
        });
        
        if ((this.metadata)) {
            this.selectAll = (this.metadata.countries.length === this.allCountries.length);
        }

        this.setSelectedDropdown(this.metadata ? this.metadata.metadataType : MetadataType.Source);
        this.allCountries.forEach(item => {
            let countriesGroup: FormGroup = this.metadataForm.controls["countries"] as FormGroup;
            let selected: MetaCountry = this.metadata? this.metadata.countries.find((mc) => {
                return mc.countryId === item.id;
            }) : undefined;
            let isDisabled: boolean = selected ? selected.isUsed : false;
            countriesGroup.addControl(
                item.id.toString(), new FormControl({ value: selected !== undefined, disabled: isDisabled }));
        });
        
        // Set loading to false after all data is loaded and form is initialized
        this.isLoading = false;
    }

    typeSelected(event: DropDownItem): void {
        this.selectedType = event;
        this.metadataForm.controls['type'].setValue(event.id);
        if (event.id === this.metadataType.Source) {
            this.addUrlControl();
        } else {
            this.metadataForm.removeControl("url");
        }
    }

    onTypeChange(event: any): void {
        const selectedTypeId = parseInt(event.target.value);
        const selectedType = this.metadataTypes.find(type => type.id === selectedTypeId);
        if (selectedType) {
            this.typeSelected(selectedType);
        }
    }

    addUrlControl(): void {
        this.metadataForm.addControl("url", new FormControl({ value: this.metadata ? (this.metadata as SourceMetadata).url : "", disabled: this.disabled }, [Validators.pattern(AppSettings.urlRegExp.source)]));
    }

    saveMetadata(form: any): void {
        if (form) {
            let raw: any = this.metadataForm.getRawValue();
            if (this.selectedType && this.selectedType.id === this.metadataType.Source) {
                let newMetadata: SourceMetadata = new SourceMetadata();
                newMetadata.id = raw.id;
                newMetadata.name = raw.name;
                newMetadata.disabled = raw.disabled;
                newMetadata.url = (raw as SourceMetadata).url;
                let selectedCountryKeys: MetaCountry[] = new Array<MetaCountry>();
                for (const key of Object.keys(raw.countries)) {
                    if (raw.countries[key] === true) {
                        selectedCountryKeys.push({ countryId: Number(key), isUsed:false } as MetaCountry);
                    }
                }
                newMetadata.countries = selectedCountryKeys;
                if (newMetadata.id) {
                    this.metadataService.update(newMetadata).subscribe((res) => {
                        this.metadataForm.reset();
                        this.toastr.success("Metadata saved successfully");
                        this.router.navigate(["/categories/managemetadata"]);
                    });
                } else {
                    this.metadataService.create(newMetadata).subscribe((res) => {
                        this.metadataForm.reset();
                        this.toastr.success("Metadata created successfully");
                        this.router.navigate(["/categories/managemetadata"]);
                    });
                }
            }
        }
    }

    setSelectedDropdown(typeId: number): void {
        let matchingType: DropDownItem = this.metadataTypes.find(c => c.id === typeId);
        if (matchingType) {
            this.selectedType = matchingType;
            this.activeType = [{ id: matchingType.id, text: matchingType.text }];
            this.typeSelected(matchingType);
        }
    }

    onTypeSelected(event: any): void {
        if (event) {
            const selectedType = this.metadataTypes.find(t => t.id === event.id);
            if (selectedType) {
                this.typeSelected(selectedType);
            }
        }
    }

    onTypeRemoved(): void {
        this.selectedType = null;
        this.activeType = [];
        this.metadataForm.controls['type'].setValue(null);
        this.metadataForm.removeControl("url");
    }

    public selectAllCountries(): void {
        this.selectAll = !this.selectAll;
        let countriesGroup: FormGroup = this.metadataForm.controls["countries"] as FormGroup;
        Object.keys(countriesGroup.controls).forEach(key => {
            if (!countriesGroup.controls[key].disabled) {
                countriesGroup.controls[key].setValue(this.selectAll);
            }
        });
    }

    @HostListener("window:beforeunload")
    canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
        if (this.metadataForm.dirty) {
            this.confirmModal.openModal();
            return false;
        } else {
            return true;
        }
    }
    onAccept(): void {
        // reset the the original back to whats currenlty in itemConfigSet, this allows to pass check  in candeactivate
       this.metadataForm.reset();
        this.router.navigate([this.navigatingTo]);
        this.confirmModal.closeModal();
    }

    onDecline(): void {
        this.confirmModal.closeModal();
    }

    navigatingTo: string;
}




